import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, BookOpen, Eye, CreditCard,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

import { StatCard } from '../components/admin/StatCard';
import { OverviewTab } from '../components/admin/tabs/OverviewTab';
import { StoriesTab } from '../components/admin/tabs/StoriesTab';
import { PendingTab } from '../components/admin/tabs/PendingTab';
import { UsersTab } from '../components/admin/tabs/UsersTab';
import { AffiliatesTab } from '../components/admin/tabs/AffiliatesTab';
import { NotificationsTab } from '../components/admin/tabs/NotificationsTab';
import { PaymentsTab } from '../components/admin/tabs/PaymentsTab';
import { DiscountsTab } from '../components/admin/tabs/DiscountsTab';
import { ReferralsTab } from '../components/admin/tabs/ReferralsTab';
import { AffiliateModal } from '../components/admin/modals/AffiliateModal';
import { UserSubscriptionModal } from '../components/admin/modals/UserSubscriptionModal';
import { DeleteAllStoriesDialog } from '../components/admin/modals/DeleteAllStoriesDialog';
import { UserActivityModal } from '../components/admin/modals/UserActivityModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlatformAdmin = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [topStories, setTopStories] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingStories, setPendingStories] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAffiliateModal, setShowAffiliateModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [subscriptionAction, setSubscriptionAction] = useState('extend');
  const [subscriptionDays, setSubscriptionDays] = useState(30);
  const [subscriptionReason, setSubscriptionReason] = useState('');
  const [affiliateForm, setAffiliateForm] = useState({
    name: '',
    code: '',
    email: '',
    discount_percentage: 20,
    commission_percentage: 15,
  });
  const [copiedCode, setCopiedCode] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState([]);
  const [approvedStories, setApprovedStories] = useState([]);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [paymentsData, setPaymentsData] = useState(null);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [newDiscount, setNewDiscount] = useState({ code: '', discount_type: 'percentage', discount_value: '', max_uses: '', expires_at: '' });
  const [creatingDiscount, setCreatingDiscount] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, topRes, usersRes, pendingRes, affiliatesRes, emailRes, storiesRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/top-stories?limit=10`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/stories/pending`, { headers }),
        axios.get(`${API}/affiliates`, { headers }),
        axios.get(`${API}/admin/email-notifications`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/stories?approved_only=true`),
        axios.get(`${API}/admin/payments`, { headers }).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setTopStories(topRes.data);
      setUsers(usersRes.data);
      setPendingStories(pendingRes.data);
      setAffiliates(affiliatesRes.data);
      setEmailNotifications(emailRes.data);
      setApprovedStories(storiesRes.data);
      setPaymentsData(paymentsRes.data);
      try {
        const discountRes = await axios.get(`${API}/admin/discount-codes`, { headers });
        setDiscountCodes(discountRes.data.codes || []);
      } catch (e) { /* ignore */ }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Kon gegevens niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveStory = async (storyId) => {
    try {
      await axios.post(`${API}/stories/${storyId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setPendingStories(stories => stories.filter(s => s.id !== storyId));
      setStats(s => s ? { ...s, total_stories: s.total_stories + 1 } : s);
      toast.success('Verhaal goedgekeurd');
    } catch (error) { toast.error('Actie mislukt'); }
  };

  const handleRejectStory = async (storyId) => {
    try {
      await axios.delete(`${API}/stories/${storyId}`, { headers: { Authorization: `Bearer ${token}` } });
      setPendingStories(stories => stories.filter(s => s.id !== storyId));
      toast.success('Verhaal verwijderd');
    } catch (error) { toast.error('Actie mislukt'); }
  };

  const handleToggleAdmin = async (userId) => {
    try {
      const response = await axios.post(`${API}/admin/users/${userId}/toggle-admin`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(us => us.map(u => u.id === userId ? { ...u, is_admin: response.data.is_admin } : u));
      toast.success(response.data.message);
    } catch (error) { toast.error('Actie mislukt'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Weet je zeker dat je deze gebruiker wilt verwijderen?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(us => us.filter(u => u.id !== userId));
      toast.success('Gebruiker verwijderd');
    } catch (error) { toast.error(error.response?.data?.detail || 'Actie mislukt'); }
  };

  const handleCreateAffiliate = async () => {
    if (!affiliateForm.name || !affiliateForm.code) {
      toast.error('Naam en code zijn verplicht');
      return;
    }
    try {
      const response = await axios.post(`${API}/affiliates`, affiliateForm, { headers: { Authorization: `Bearer ${token}` } });
      setAffiliates(prev => [...prev, response.data]);
      setShowAffiliateModal(false);
      setAffiliateForm({ name: '', code: '', email: '', discount_percentage: 20, commission_percentage: 15 });
      toast.success('Affiliate aangemaakt');
    } catch (error) { toast.error(error.response?.data?.detail || 'Kon affiliate niet aanmaken'); }
  };

  const handleToggleAffiliate = async (affiliateId, currentStatus) => {
    try {
      const response = await axios.put(`${API}/affiliates/${affiliateId}`, { is_active: !currentStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setAffiliates(prev => prev.map(a => a.id === affiliateId ? response.data : a));
      toast.success(`Affiliate ${response.data.is_active ? 'geactiveerd' : 'gedeactiveerd'}`);
    } catch (error) { toast.error('Actie mislukt'); }
  };

  const handleDeleteAffiliate = async (affiliateId) => {
    if (!window.confirm('Weet je zeker dat je deze affiliate wilt verwijderen?')) return;
    try {
      await axios.delete(`${API}/affiliates/${affiliateId}`, { headers: { Authorization: `Bearer ${token}` } });
      setAffiliates(prev => prev.filter(a => a.id !== affiliateId));
      toast.success('Affiliate verwijderd');
    } catch (error) { toast.error('Kon affiliate niet verwijderen'); }
  };

  const handleSendNotification = async (storyId) => {
    if (!window.confirm('Weet je zeker dat je alle gebruikers wilt notificeren over dit verhaal?')) return;
    setSendingNotification(true);
    try {
      const response = await axios.post(`${API}/admin/notify-new-story`, { story_id: storyId }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      const emailRes = await axios.get(`${API}/admin/email-notifications`, { headers: { Authorization: `Bearer ${token}` } });
      setEmailNotifications(emailRes.data);
    } catch (error) { toast.error(error.response?.data?.detail || 'Kon notificatie niet versturen'); }
    finally { setSendingNotification(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
    toast.success('Code gekopieerd');
  };

  const handleToggleBlock = async (userId) => {
    try {
      const response = await axios.post(`${API}/admin/users/${userId}/toggle-block`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(us => us.map(u => u.id === userId ? { ...u, is_blocked: response.data.is_blocked } : u));
      toast.success(response.data.message);
    } catch (error) { toast.error(error.response?.data?.detail || 'Actie mislukt'); }
  };

  const handleExportUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/export/users`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gebruikers_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV geëxporteerd');
    } catch (error) { toast.error('Export mislukt'); }
  };

  const openUserModal = (targetUser) => {
    setSelectedUser(targetUser);
    setSubscriptionAction('extend');
    setSubscriptionDays(30);
    setSubscriptionReason('');
    setShowUserModal(true);
  };

  const handleSubscriptionAction = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/users/${selectedUser.id}/subscription`,
        { action: subscriptionAction, days: subscriptionDays, reason: subscriptionReason || undefined },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(response.data.message);
      setShowUserModal(false);
      const usersRes = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(usersRes.data);
    } catch (error) { toast.error(error.response?.data?.detail || 'Actie mislukt'); }
    finally { setActionLoading(false); }
  };

  const handleViewActivity = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}/activity`, { headers: { Authorization: `Bearer ${token}` } });
      setUserActivity(response.data);
      setShowActivityModal(true);
    } catch (error) { toast.error('Kon activiteit niet laden'); }
  };

  const handleToggleFree = async (storyId) => {
    try {
      const response = await axios.post(`${API}/stories/${storyId}/toggle-free`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setTopStories(stories => stories.map(s => s.id === storyId ? { ...s, is_free: response.data.is_free } : s));
      toast.success(response.data.message);
    } catch (error) { toast.error(error.response?.data?.detail || 'Actie mislukt'); }
  };

  const handleDeleteAllStories = async () => {
    setDeletingAll(true);
    try {
      const response = await axios.delete(`${API}/admin/stories/all`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      setShowDeleteAllDialog(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Verwijderen mislukt'); }
    finally { setDeletingAll(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4" data-testid="platform-admin-loading">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
          </div>
        </div>
      </div>
    );
  }

  const ctx = {
    // state
    stats, topStories, users, pendingStories, affiliates,
    approvedStories, emailNotifications, paymentsData, discountCodes,
    newDiscount, creatingDiscount,
    selectedUser, showUserModal, showAffiliateModal, showActivityModal,
    showDeleteAllDialog, userActivity,
    subscriptionAction, subscriptionDays, subscriptionReason, actionLoading,
    affiliateForm, copiedCode, sendingNotification, deletingAll,
    token, API, user,
    // setters
    setStats, setTopStories, setUsers, setPendingStories, setAffiliates,
    setDiscountCodes, setNewDiscount, setCreatingDiscount,
    setSelectedUser, setShowUserModal, setShowAffiliateModal, setShowActivityModal,
    setShowDeleteAllDialog, setSubscriptionAction, setSubscriptionDays, setSubscriptionReason,
    setAffiliateForm,
    // handlers
    handleApproveStory, handleRejectStory,
    handleToggleAdmin, handleDeleteUser, handleToggleBlock,
    handleExportUsers, openUserModal, handleViewActivity, handleSubscriptionAction,
    handleCreateAffiliate, handleToggleAffiliate, handleDeleteAffiliate,
    handleSendNotification, copyCode,
    handleToggleFree, handleDeleteAllStories,
    // external libs passed for DiscountsTab's inline fetches
    axios, toast,
  };

  return (
    <div className="min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4" data-testid="platform-admin-page">
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-8">
            <h1 className="font-display text-4xl text-foreground">Platform Beheer</h1>
            <p className="text-muted-foreground mt-1">Overzicht van je platform</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Gebruikers" value={stats?.total_users || 0} />
            <StatCard icon={BookOpen} label="Verhalen" value={stats?.total_stories || 0} subtext={`${pendingStories.length} wachtend`} />
            <StatCard icon={Eye} label="Totaal gelezen" value={stats?.total_reads?.toLocaleString() || 0} />
            <StatCard icon={CreditCard} label="Omzet (maand)" value={`€${stats?.revenue_estimate?.toFixed(2) || '0.00'}`} subtext={`${stats?.active_subscriptions || 0} actief, ${stats?.trial_subscriptions || 0} trial`} />
          </div>

          <Tabs defaultValue="overview" className="space-y-6">
            <div className="overflow-x-auto -mx-4 px-4 pb-1">
              <TabsList className="bg-muted/50 p-1 inline-flex w-max min-w-full">
                <TabsTrigger value="overview" className="rounded-lg text-xs sm:text-sm">Overzicht</TabsTrigger>
                <TabsTrigger value="pending" className="rounded-lg text-xs sm:text-sm">Goedkeuren {pendingStories.length > 0 && `(${pendingStories.length})`}</TabsTrigger>
                <TabsTrigger value="users" className="rounded-lg text-xs sm:text-sm">Gebruikers</TabsTrigger>
                <TabsTrigger value="stories" className="rounded-lg text-xs sm:text-sm" data-testid="stories-tab">Verhalen</TabsTrigger>
                <TabsTrigger value="payments" className="rounded-lg text-xs sm:text-sm" data-testid="payments-tab">Betalingen</TabsTrigger>
                <TabsTrigger value="discounts" className="rounded-lg text-xs sm:text-sm" data-testid="discounts-tab">Kortingscodes</TabsTrigger>
                <TabsTrigger value="affiliates" className="rounded-lg text-xs sm:text-sm">Affiliates</TabsTrigger>
                <TabsTrigger value="notifications" className="rounded-lg text-xs sm:text-sm">Notificaties</TabsTrigger>
                <TabsTrigger value="referrals" className="rounded-lg text-xs sm:text-sm" data-testid="referrals-tab">Referrals</TabsTrigger>
              </TabsList>
            </div>

            <OverviewTab ctx={ctx} />
            <StoriesTab ctx={ctx} />
            <PendingTab ctx={ctx} />
            <UsersTab ctx={ctx} />
            <AffiliatesTab ctx={ctx} />
            <NotificationsTab ctx={ctx} />
            <PaymentsTab ctx={ctx} />
            <DiscountsTab ctx={ctx} />
            <ReferralsTab ctx={ctx} />
          </Tabs>

          <AffiliateModal ctx={ctx} />
          <UserSubscriptionModal ctx={ctx} />
          <DeleteAllStoriesDialog ctx={ctx} />
          <UserActivityModal ctx={ctx} />
        </motion.div>
      </div>
    </div>
  );
};

export default PlatformAdmin;
